import * as React from 'react'
import { ArrowUpRight, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  useSwitchBranch,
  type TaggedNode,
  type TaggedRel
} from '@axilon-apps/domains-ontology-ui-state'
import { plantHref } from '@atbash-labs/plantview-links'
import {
  SourceDocChip,
  SourceDocEndpointsChip,
  SourceDocViewer,
  useSourceDocViewer,
  type SourceDocRef
} from '../../../components/SourceDocViewer'
import { Badge } from '../../../components/ui/badge'
import { entityDisplayName } from '../../../components/Branches/entity-display'
import { cn } from '../../../lib/utils'
import { designPixelsToRem } from '../../../lib/ui-density'
import { nodeSourceFields, type NodeSourceFields } from './node-source-fields'

// The plant containment hierarchy, keyed on relationship type → which endpoint
// of the edge is the parent. Derived from the Plant-Viewer cyphers:
//   Site -[HAS_LINE]-> Line, Line -[HAS_SUBLINE]-> Line,
//   Line -[HAS_EQUIPMENT]-> Equipment, RobotController -[CONTROLS]-> Equipment.
const CONTAINMENT: Record<string, 'start' | 'end'> = {
  HAS_LINE: 'start',
  HAS_SUBLINE: 'start',
  HAS_EQUIPMENT: 'start',
  CONTROLS: 'end'
}

// Node labels that form the structural skeleton, in render order.
type TreeLabel = 'Site' | 'Line' | 'Equipment' | 'RobotController'

const LABEL_ORDER: Record<TreeLabel, number> = {
  Site: 0,
  Line: 1,
  Equipment: 2,
  RobotController: 3
}
const TREE_LABELS = new Set<TreeLabel>(Object.keys(LABEL_ORDER) as TreeLabel[])

// "Major" entities whose lateral (non-containment) relationships are worth
// surfacing inline. Excludes tag/observable leaves so the tree stays legible.
const MAJOR_LABELS = new Set([
  'Site',
  'Line',
  'Equipment',
  'RobotController',
  'RobotArm',
  'Controller',
  'Compute',
  'Process',
  'Area'
])

interface TreeNode extends NodeSourceFields {
  id: string
  label: TreeLabel
  name: string
}
interface NamedEndpoint extends NodeSourceFields {
  label: string
  name: string
}
interface LateralRel {
  key: string
  type: string
  dir: 'out' | 'in'
  otherId: string
  otherLabel: string
  otherName: string
}

interface StructureTreeProps {
  nodes: readonly TaggedNode[]
  relationships: readonly TaggedRel[]
  /** The job branch; the open button switches to it before opening the viewer. */
  branchId?: string
  /** Ambient ingest job — required for source chips. */
  jobId?: string
}

function structuralLabel(labels: string[] | undefined): TreeLabel | undefined {
  return labels?.find((label): label is TreeLabel => TREE_LABELS.has(label as TreeLabel))
}

function majorLabel(labels: string[] | undefined): string | undefined {
  return labels?.find((l) => MAJOR_LABELS.has(l))
}

/**
 * Nests the agent's created structural nodes (Site → Line → Equipment →
 * RobotController) into a collapsible tree, built from the branch compare's
 * added relationships. Each node also lists its non-containment relationships to
 * other major entities (FEEDS, MONITORED_BY, MAPS_TO_CONTROLLER, …). Nodes whose
 * parent already lives on main surface under "Attached to existing structure".
 */
export function StructureTree({ nodes, relationships, branchId, jobId }: StructureTreeProps) {
  const navigate = useNavigate()
  const { switchBranch, isOpening } = useSwitchBranch()
  const { openSource, viewerProps } = useSourceDocViewer()

  const { siteRoots, orphanRoots, childrenOf, nodesById, namedById, lateralByNode } =
    React.useMemo(() => {
      const nodesById = new Map<string, TreeNode>()
      // Every added major node, for naming + sourcing the far end of a lateral.
      const namedById = new Map<string, NamedEndpoint>()
      for (const n of nodes) {
        const name = entityDisplayName(n)
        const source = nodeSourceFields(n)
        const structural = structuralLabel(n.labels)
        if (structural) nodesById.set(n.id, { id: n.id, label: structural, name, ...source })
        const major = majorLabel(n.labels)
        if (major) namedById.set(n.id, { label: major, name, ...source })
      }

      const parentOf = new Map<string, string>()
      const lateralByNode = new Map<string, LateralRel[]>()
      const pushLateral = (nodeId: string, rel: LateralRel): void => {
        const arr = lateralByNode.get(nodeId)
        if (arr) arr.push(rel)
        else lateralByNode.set(nodeId, [rel])
      }

      for (const r of relationships) {
        const dir = CONTAINMENT[r.type]
        if (dir) {
          const parentId = dir === 'start' ? r.startId : r.endId
          const childId = dir === 'start' ? r.endId : r.startId
          if (nodesById.has(parentId) && nodesById.has(childId) && !parentOf.has(childId)) {
            parentOf.set(childId, parentId)
          }
          continue
        }
        // Lateral (non-containment) edge: surface it under any structural endpoint,
        // when the far end is a named major entity.
        const startMajor = namedById.get(r.startId)
        const endMajor = namedById.get(r.endId)
        if (nodesById.has(r.startId) && endMajor) {
          pushLateral(r.startId, {
            key: `${r.type}:${r.endId}:out`,
            type: r.type,
            dir: 'out',
            otherId: r.endId,
            otherLabel: endMajor.label,
            otherName: endMajor.name
          })
        }
        if (nodesById.has(r.endId) && startMajor) {
          pushLateral(r.endId, {
            key: `${r.type}:${r.startId}:in`,
            type: r.type,
            dir: 'in',
            otherId: r.startId,
            otherLabel: startMajor.label,
            otherName: startMajor.name
          })
        }
      }

      const byOrder = (a: string, b: string): number => {
        const A = nodesById.get(a)!
        const B = nodesById.get(b)!
        return (
          (LABEL_ORDER[A.label] ?? 9) - (LABEL_ORDER[B.label] ?? 9) ||
          A.name.localeCompare(B.name) ||
          a.localeCompare(b)
        )
      }
      const childrenOf = new Map<string, string[]>()
      for (const [child, parent] of parentOf) {
        const arr = childrenOf.get(parent)
        if (arr) arr.push(child)
        else childrenOf.set(parent, [child])
      }
      for (const arr of childrenOf.values()) arr.sort(byOrder)
      for (const arr of lateralByNode.values()) {
        arr.sort((a, b) => a.type.localeCompare(b.type) || a.otherName.localeCompare(b.otherName))
      }

      const roots = [...nodesById.keys()].filter((id) => !parentOf.has(id)).sort(byOrder)
      const siteRoots = roots.filter((id) => nodesById.get(id)!.label === 'Site')
      const orphanRoots = roots.filter((id) => nodesById.get(id)!.label !== 'Site')
      return { siteRoots, orphanRoots, childrenOf, nodesById, namedById, lateralByNode }
    }, [nodes, relationships])

  const openInPlantViewer = async (href: string): Promise<void> => {
    if (!branchId) return
    try {
      await switchBranch(branchId)
      navigate(href)
    } catch {
      // Open/build failed — leave the active branch (and the page) untouched.
    }
  }

  if (nodesById.size === 0) return null

  const showOrphanLabel = siteRoots.length > 0 && orphanRoots.length > 0
  const renderRoots = (ids: string[]) =>
    ids.map((id) => (
      <TreeRow
        key={id}
        id={id}
        depth={0}
        childrenOf={childrenOf}
        nodesById={nodesById}
        namedById={namedById}
        lateralByNode={lateralByNode}
        disabled={isOpening}
        jobId={jobId}
        onOpen={openInPlantViewer}
        onOpenSource={openSource}
      />
    ))

  return (
    // Fills the remaining height of the fixed CreatedEntities card and scrolls
    // internally, so expanding nodes never grows the card.
    <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border p-1">
      {renderRoots(siteRoots)}
      {orphanRoots.length > 0 ? (
        <>
          {showOrphanLabel ? (
            <div className="px-2 pt-2 pb-1 text-11 font-medium tracking-[0.04em] text-muted-foreground/80 uppercase">
              Attached to existing structure
            </div>
          ) : null}
          {renderRoots(orphanRoots)}
        </>
      ) : null}
      <SourceDocViewer {...viewerProps} />
    </div>
  )
}

interface TreeRowProps {
  id: string
  depth: number
  childrenOf: Map<string, string[]>
  nodesById: Map<string, TreeNode>
  namedById: Map<string, NamedEndpoint>
  lateralByNode: Map<string, LateralRel[]>
  disabled: boolean
  jobId?: string
  onOpen: (href: string) => void
  onOpenSource: (args: SourceDocRef) => void
}

function TreeRow({
  id,
  depth,
  childrenOf,
  nodesById,
  namedById,
  lateralByNode,
  disabled,
  jobId,
  onOpen,
  onOpenSource
}: TreeRowProps) {
  const node = nodesById.get(id)
  const [open, setOpen] = React.useState(false)
  if (!node || depth > 24) return null

  const kids = childrenOf.get(id) ?? []
  const lateral = lateralByNode.get(id) ?? []
  const hasKids = kids.length > 0
  const hasChildren = hasKids || lateral.length > 0
  const href = plantHref(node.id, node.label, node)
  const indent = depth * 14

  return (
    <div>
      <div className="group flex items-center gap-1 rounded-md hover:bg-accent/50">
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={() => setOpen((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
            style={{ paddingLeft: designPixelsToRem(indent) }}
          >
            <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <ChevronRight
                aria-hidden
                className={cn(
                  'size-3.5 transition-transform duration-200 ease-out',
                  open && 'rotate-90'
                )}
              />
            </span>
            <Badge variant="outline" className="shrink-0 font-normal">
              {node.label}
            </Badge>
            <span className="min-w-0 truncate text-sm text-foreground" title={node.id}>
              {node.name}
            </span>
            {hasKids ? (
              <span className="font-mono text-xs text-muted-foreground tabular-nums">
                {kids.length}
              </span>
            ) : null}
          </button>
        ) : (
          <div
            className="flex min-w-0 flex-1 items-center gap-2 py-1"
            style={{ paddingLeft: designPixelsToRem(indent) }}
          >
            <span className="size-5 shrink-0" />
            <Badge variant="outline" className="shrink-0 font-normal">
              {node.label}
            </Badge>
            <span className="min-w-0 truncate text-sm text-foreground" title={node.id}>
              {node.name}
            </span>
          </div>
        )}
        <SourceDocChip
          compact
          jobId={jobId}
          sourceFile={node.sourceFile}
          sourcePage={node.sourcePage}
          sourceExcerpt={node.sourceExcerpt}
          onOpen={onOpenSource}
        />
        {href ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onOpen(href)}
            title={`Open ${node.name} in Plant Viewer`}
            className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-[color,opacity] hover:bg-accent hover:text-foreground focus-visible:opacity-100 disabled:opacity-30 group-hover:opacity-100"
          >
            <ArrowUpRight aria-hidden className="size-3.5" />
          </button>
        ) : null}
      </div>

      {hasChildren ? (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">
            {lateral.map((rel) => {
              const other = namedById.get(rel.otherId)
              return (
                <div
                  key={rel.key}
                  className="flex items-center gap-1.5 py-0.5 text-xs text-muted-foreground"
                  style={{ paddingLeft: designPixelsToRem(indent + 20) }}
                >
                  <span aria-hidden className="text-muted-foreground/60">
                    {rel.dir === 'out' ? '→' : '←'}
                  </span>
                  <span className="font-mono">{rel.type}</span>
                  <span
                    className="min-w-0 flex-1 truncate text-foreground/70"
                    title={rel.otherName}
                  >
                    {rel.otherName}
                  </span>
                  <SourceDocEndpointsChip
                    jobId={jobId}
                    endpoints={[
                      {
                        label: node.label,
                        sourceFile: node.sourceFile,
                        sourcePage: node.sourcePage,
                        sourceExcerpt: node.sourceExcerpt
                      },
                      {
                        label: rel.otherLabel,
                        sourceFile: other?.sourceFile,
                        sourcePage: other?.sourcePage,
                        sourceExcerpt: other?.sourceExcerpt
                      }
                    ]}
                    onOpen={onOpenSource}
                  />
                </div>
              )
            })}
            {kids.map((kid) => (
              <TreeRow
                key={kid}
                id={kid}
                depth={depth + 1}
                childrenOf={childrenOf}
                nodesById={nodesById}
                namedById={namedById}
                lateralByNode={lateralByNode}
                disabled={disabled}
                jobId={jobId}
                onOpen={onOpen}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default StructureTree
